import { Check, Copy, Gift } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cn } from 'lib/utils'
import { useReferralProgram } from './use-referral-program'
import { useReferralShare, type ReferralSurface } from './use-referral-share'

interface ReferralInvitePromptProps {
  /** Which surface this prompt renders on — drives the analytics `surface`. */
  surface: Extract<ReferralSurface, 'post_publish_success' | 'credits_exhausted'>
  className?: string
}

/**
 * Small, unobtrusive "Earn credits — invite a friend" entry for share surfaces
 * outside Settings (post-publish success, credits-exhausted). Renders NOTHING
 * unless the `referral_program` gate is on AND the server reports the program
 * enabled with a usable link — so it never shows for cohorts who can't refer.
 *
 * The label links through to the full Settings page; the trailing button copies
 * the referral link (fires `REFERRAL_SHARED` with the right `surface`). Two
 * sibling buttons rather than a nested one to keep the markup valid.
 */
export function ReferralInvitePrompt({ surface, className }: ReferralInvitePromptProps) {
  const navigate = useNavigate()
  const { enabled, info } = useReferralProgram()
  const { copied, copyLink } = useReferralShare(surface)

  if (!enabled || !info?.link) return null

  const link = info.link
  const label =
    info.referrerCredits > 0
      ? `Invite a friend, earn ${info.referrerCredits} credits`
      : 'Earn credits, invite a friend'

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-[10px] border border-line bg-bg-3 px-3 py-2',
        className
      )}
    >
      <Gift size={15} className="shrink-0 text-acid" aria-hidden />
      <button
        type="button"
        onClick={() => navigate('/referrals')}
        className="min-w-0 flex-1 text-left text-[12.5px] leading-tight text-paper hover:text-paper"
      >
        {label}
      </button>
      <button
        type="button"
        onClick={() => copyLink(link)}
        className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-paper-dim hover:text-paper"
      >
        {copied ? (
          <>
            <Check size={12} className="text-acid" /> Copied
          </>
        ) : (
          <>
            <Copy size={12} /> Copy link
          </>
        )}
      </button>
    </div>
  )
}
